import React, { useState, useEffect } from 'react';
import { MdClose, MdPerson, MdLocationOn, MdDateRange } from 'react-icons/md';
import axios from 'axios';
import { API_BASE_URL, API_CONFIG } from '../../config/api';

const AddRevenueOfficerModal = ({ isOpen, onClose, showPopup, fetchData, userMmdaId, user, onSuccess, onError }) => {
  const [entryMode, setEntryMode] = useState('existing'); // 'existing' or 'manual'
  const [formData, setFormData] = useState({
    user_id: '', // Selected user ID for existing mode
    zone_id: '',
    assignment_date: new Date().toISOString().split('T')[0],
    // Manual entry fields
    first_name: '',
    middle_name: '',
    last_name: '',
    phone: '',
    email: '',
  });
  const [zones, setZones] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [checkingExistence, setCheckingExistence] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictData, setConflictData] = useState(null);



  // Comprehensive conflict checking function
  const checkConflicts = async (email, phone, firstName, lastName) => {
    setCheckingExistence(true);
    try {
      const conflicts = {
        emailConflict: null,
        phoneConflict: null,
        nameConflict: null
      };

      // Check email conflict
      if (email && email.trim() !== '') {
        const emailCheckRes = await axios.get(`${API_BASE_URL}/revenue-officers?email=${email}`, API_CONFIG);
        if (emailCheckRes.data && emailCheckRes.data.length > 0) {
          conflicts.emailConflict = emailCheckRes.data[0];
        }
      }

      // Check phone conflict
      if (phone && phone.trim() !== '') {
        const phoneCheckRes = await axios.get(`${API_BASE_URL}/revenue-officers?phone=${phone}`, API_CONFIG);
        if (phoneCheckRes.data && phoneCheckRes.data.length > 0) {
          conflicts.phoneConflict = phoneCheckRes.data[0];
        }
      }

      // Check name conflict
      if (firstName && lastName) {
        const fullName = `${firstName} ${lastName}`.trim();
        const nameCheckRes = await axios.get(`${API_BASE_URL}/revenue-officers`, API_CONFIG);
        const existingOfficer = nameCheckRes.data.find(officer => {
          const officerFullName = `${officer.first_name} ${officer.last_name}`.trim();
          return officerFullName.toLowerCase() === fullName.toLowerCase();
        });
        if (existingOfficer) {
          conflicts.nameConflict = existingOfficer;
        }
      }

      return conflicts;
    } catch (error) {

      return {
        emailConflict: null,
        phoneConflict: null,
        nameConflict: null
      };
    } finally {
      setCheckingExistence(false);
    }
  };

  // Fetch data for dropdowns
  const fetchDropdownData = async () => {
    setDataLoading(true);
    try {
      // Fetch zones
      const zonesRes = await axios.get(`${API_BASE_URL}/zones?userRole=MMDA&mmda_id=${userMmdaId}`, API_CONFIG);
      setZones(zonesRes.data);

      // Fetch all Field Officers from the same MMDA (only for existing mode)
      if (entryMode === 'existing') {
        const usersRes = await axios.get(`${API_BASE_URL}/users?userRole=MMDA&mmda_id=${userMmdaId}&targetRole=field_officer`, API_CONFIG);

        setUsers(usersRes.data);
      }
    } catch (error) {

      showPopup('Failed to load data', 'error');
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchDropdownData();
    }
  }, [isOpen, entryMode]);

  // Remove group filtering since we're not using groups in this modal

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'user_id' && entryMode === 'existing') {
      // Find the selected user and store their details
      const user = users.find(u => u.user_id.toString() === value);
      setSelectedUser(user);
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let officerData;
      let officerName;

      if (entryMode === 'existing') {
        // Existing user mode
        if (!selectedUser) {
          showPopup('Please select a user to assign as revenue officer', 'error');
          setLoading(false);
          return;
        }

        officerName = selectedUser.full_name;
        officerData = {
          user_id: parseInt(formData.user_id),
          zone_id: parseInt(formData.zone_id),
          assignment_date: formData.assignment_date,
          mmda_id: userMmdaId,
          first_name: selectedUser.full_name.split(' ')[0] || selectedUser.full_name,
          last_name: selectedUser.full_name.split(' ').slice(1).join(' ') || '',
          middle_name: '',
          phone: selectedUser.phone,
          email: selectedUser.email,
          status: 'active',
          created_by: user?.user_id 
        };

        // Check for conflicts using comprehensive checking
        const conflicts = await checkConflicts(
          selectedUser.email,
          selectedUser.phone,
          selectedUser.full_name.split(' ')[0],
          selectedUser.full_name.split(' ').slice(1).join(' ')
        );

        if (conflicts.emailConflict || conflicts.phoneConflict || conflicts.nameConflict) {
          setConflictData({
            conflicts,
            userData: selectedUser,
            mode: 'existing'
          });
          setShowConflictModal(true);
          setLoading(false);
          return;
        }
      } else {
        // Manual entry mode
        if (!formData.first_name || !formData.last_name || !formData.email) {
          showPopup('Please fill in all required fields (First Name, Last Name, Email)', 'error');
          setLoading(false);
          return;
        }

        officerName = `${formData.first_name} ${formData.last_name}`;

        // Check for conflicts using comprehensive checking
        const conflicts = await checkConflicts(formData.email, formData.phone, formData.first_name, formData.last_name);

        if (conflicts.emailConflict || conflicts.phoneConflict || conflicts.nameConflict) {
          setConflictData({
            conflicts,
            userData: formData,
            mode: 'manual'
          });
          setShowConflictModal(true);
          setLoading(false);
          return;
        }

        officerData = {
          zone_id: parseInt(formData.zone_id),
          assignment_date: formData.assignment_date,
          mmda_id: userMmdaId,
          first_name: formData.first_name,
          middle_name: formData.middle_name || '',
          last_name: formData.last_name,
          phone: formData.phone || '',
          email: formData.email,
          status: 'active',
          created_by: user?.user_id 
        };
      }




      const response = await axios.post(`${API_BASE_URL}/revenue-officers`, officerData, API_CONFIG);

      if (response.status === 201) {
        // Reset form based on entry mode
        if (entryMode === 'existing') {
          setFormData({
            user_id: '',
            zone_id: '',
            assignment_date: new Date().toISOString().split('T')[0],
            first_name: '',
            middle_name: '',
            last_name: '',
            phone: '',
            email: '',
          });
          setSelectedUser(null);
        } else {
          setFormData({
            user_id: '',
            zone_id: '',
            assignment_date: new Date().toISOString().split('T')[0],
            first_name: '',
            middle_name: '',
            last_name: '',
            phone: '',
            email: '',
          });
        }

        onClose();

        // Show success modal
        if (onSuccess) {
          const actionType = entryMode === 'existing' ? 'Assigned' : 'Added';
          onSuccess(actionType, 'Revenue Officer', officerName, 'Officer has been successfully added and can now start collecting revenue.');
        } else {
          showPopup('Revenue officer created successfully!', 'success');
          fetchData();
        }
      }
    } catch (error) {

      const officerName = selectedUser ? selectedUser.full_name : 'Officer';

      // Handle specific error types
      let errorMessage = 'Failed to assign revenue officer';



      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;

      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;

      } else if (error.message.includes('duplicate key')) {
        if (error.message.includes('phone')) {
          errorMessage = `Phone number ${selectedUser?.phone} is already assigned to another revenue officer`;
        } else if (error.message.includes('email')) {
          errorMessage = `Email ${selectedUser?.email} is already assigned to another revenue officer`;
        } else {
          errorMessage = `${officerName} is already assigned as a revenue officer`;
        }

      } else if (error.message.includes('unique constraint')) {
        if (error.message.includes('phone')) {
          errorMessage = `Phone number ${selectedUser?.phone} is already assigned to another revenue officer`;
        } else if (error.message.includes('email')) {
          errorMessage = `Email ${selectedUser?.email} is already assigned to another revenue officer`;
        } else {
          errorMessage = `${officerName} is already assigned as a revenue officer`;
        }

      } else if (error.response?.status === 409) {
        errorMessage = `${officerName} is already assigned as a revenue officer`;

      } else {
        errorMessage = error.message || 'Failed to assign revenue officer';

      }

      // Check if it's a duplicate error based on various indicators
      const isDuplicateError =
        errorMessage.includes('already assigned') ||
        errorMessage.includes('duplicate') ||
        errorMessage.includes('unique constraint') ||
        error.response?.status === 409;

      if (isDuplicateError) {

        errorMessage = `${officerName} is already assigned as a revenue officer`;
      }

      // Show error modal
      if (onError) {
        onError('Assign', 'Revenue Officer', officerName, errorMessage);
      } else {
        showPopup(errorMessage, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Function to proceed with insertion despite conflicts
  const proceedWithInsertion = async () => {
    setShowConflictModal(false);
    setLoading(true);

    try {
      let officerData;
      let officerName;

      if (conflictData.mode === 'existing') {
        const selectedUser = conflictData.userData;
        officerName = selectedUser.full_name;
        officerData = {
          user_id: parseInt(formData.user_id),
          zone_id: parseInt(formData.zone_id),
          assignment_date: formData.assignment_date,
          mmda_id: userMmdaId,
          first_name: selectedUser.full_name.split(' ')[0] || selectedUser.full_name,
          last_name: selectedUser.full_name.split(' ').slice(1).join(' ') || '',
          middle_name: '',
          phone: selectedUser.phone,
          email: selectedUser.email,
          status: 'active',
          created_by: user?.user_id 
        };
      } else {
        // Manual entry mode
        const userData = conflictData.userData;
        officerName = `${userData.first_name} ${userData.last_name}`;
        officerData = {
          zone_id: parseInt(formData.zone_id),
          assignment_date: formData.assignment_date,
          mmda_id: userMmdaId,
          first_name: userData.first_name,
          middle_name: userData.middle_name || '',
          last_name: userData.last_name,
          phone: userData.phone || '',
          email: userData.email,
          status: 'active',
          created_by: user?.user_id 
        };
      }

 
      const response = await axios.post(`${API_BASE_URL}/revenue-officers`, officerData, API_CONFIG);

      if (response.status === 201) {
        // Reset form based on entry mode
        if (conflictData.mode === 'existing') {
          setFormData({
            user_id: '',
            zone_id: '',
            assignment_date: new Date().toISOString().split('T')[0],
            first_name: '',
            middle_name: '',
            last_name: '',
            phone: '',
            email: '',
          });
          setSelectedUser(null);
        } else {
          setFormData({
            user_id: '',
            zone_id: '',
            assignment_date: new Date().toISOString().split('T')[0],
            first_name: '',
            middle_name: '',
            last_name: '',
            phone: '',
            email: '',
          });
        }

        onClose();

        // Show success modal
        if (onSuccess) {
          const actionType = conflictData.mode === 'existing' ? 'Assigned' : 'Added';
          onSuccess(actionType, 'Revenue Officer', officerName, 'Officer has been successfully added despite conflicts and can now start collecting revenue.');
        } else {
          showPopup('Revenue officer created successfully!', 'success');
          fetchData();
        }
      }
    } catch (error) {

      showPopup('Failed to create revenue officer', 'error');
    } finally {
      setLoading(false);
      setConflictData(null);
    }
  };

  const handleClose = () => {
    setFormData({
      user_id: '',
      zone_id: '',
      assignment_date: new Date().toISOString().split('T')[0],
      first_name: '',
      middle_name: '',
      last_name: '',
      phone: '',
      email: '',
    });
    setSelectedUser(null);
    setEntryMode('existing'); // Reset to default mode
    setShowConflictModal(false); // Reset conflict modal
    setConflictData(null); // Clear conflict data
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 8,
        padding: 24,
        width: '90%',
        maxWidth: 500,
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 20,
          paddingBottom: 15,
          borderBottom: '2px solid #e0e0e0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <MdPerson size={24} style={{ color: '#17a2b8' }} />
            <h2 style={{ margin: 0, color: '#17a2b8', fontSize: 20 }}>Add Revenue Officer</h2>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 5,
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MdClose size={24} style={{ color: '#666' }} />
          </button>
        </div>

        {/* Entry Mode Toggle */}
        <div style={{ marginBottom: 20, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: 8, border: '1px solid #e9ecef' }}>
          <h3 style={{ margin: '0 0 12px 0', color: '#333', fontSize: 16, fontWeight: 'bold' }}>
            How would you like to add the officer?
          </h3>
          <div style={{ display: 'flex', gap: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="radio"
                name="entryMode"
                value="existing"
                checked={entryMode === 'existing'}
                onChange={(e) => setEntryMode(e.target.value)}
                style={{ margin: 0 }}
              />
              <span style={{ fontSize: 14, fontWeight: 500 }}>Select from existing users</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="radio"
                name="entryMode"
                value="manual"
                checked={entryMode === 'manual'}
                onChange={(e) => setEntryMode(e.target.value)}
                style={{ margin: 0 }}
              />
              <span style={{ fontSize: 14, fontWeight: 500 }}>Enter officer details manually</span>
            </label>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {entryMode === 'existing' ? (
            /* Existing User Selection Section */
            <div style={{ marginBottom: 24 }}>
              <h3 style={{
                margin: '0 0 16px 0',
                color: '#333',
                fontSize: 18,
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                paddingBottom: 8,
                borderBottom: '2px solid #f0f0f0'
              }}>
                <MdPerson size={22} style={{ color: '#667eea' }} />
                Select User to Assign
              </h3>

            <div>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 8,
                fontWeight: 'bold',
                color: '#333',
                fontSize: 14,
              }}>

                Select Field Officer *
              </label>
              <select
                name="user_id"
                value={formData.user_id}
                onChange={handleInputChange}
                required
                disabled={dataLoading}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #e0e0e0',
                  borderRadius: 8,
                  fontSize: 14,
                  outline: 'none',
                  boxSizing: 'border-box',
                  backgroundColor: dataLoading ? '#f5f5f5' : 'white',
                  transition: 'border-color 0.2s',
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
              >
                <option value="">
                  {dataLoading ? 'Loading users...' : 'Select a field officer to assign'}
                </option>
                {users.map(user => (
                  <option key={user.user_id} value={user.user_id}>
                    {user.full_name} | {user.email} | {user.phone}
                  </option>
                ))}
              </select>
              {!dataLoading && users.length === 0 && (
                <p style={{
                  margin: '8px 0 0 0',
                  fontSize: 12,
                  color: '#ff6b6b',
                  fontStyle: 'italic',
                  padding: '8px',
                  backgroundColor: '#fff5f5',
                  border: '1px solid #fed7d7',
                  borderRadius: 4
                }}>
                  No available field officers found. All field officers may already be assigned as revenue officers.
                </p>
              )}
            </div>

            {/* Selected User Preview */}
            {selectedUser && (
              <div style={{
                marginTop: 16,
                padding: 16,
                backgroundColor: '#f8f9fa',
                borderRadius: 8,
                border: '1px solid #e9ecef',
              }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#333', fontSize: 14, fontWeight: 'bold' }}>
                  Selected Officer Details:
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, fontSize: 12, color: '#666' }}>
                  <div>
                    <strong>Name:</strong> {selectedUser.full_name}
                  </div>
                  <div>
                    <strong>Email:</strong> {selectedUser.email}
                  </div>
                  <div>
                    <strong>Phone:</strong> {selectedUser.phone}
                  </div>
                </div>
              </div>
            )}
            </div>
          ) : (
            /* Manual Entry Section */
            <div style={{ marginBottom: 24 }}>
              <h3 style={{
                margin: '0 0 16px 0',
                color: '#333',
                fontSize: 18,
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                paddingBottom: 8,
                borderBottom: '2px solid #f0f0f0'
              }}>
                <MdPerson size={22} style={{ color: '#667eea' }} />
                Officer Information
              </h3>

              {/* Name Fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: 14 }}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '90%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 6,
                      fontSize: 14,
                      outline: 'none',
                    }}
                    placeholder="First name"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: 14 }}>
                    Middle Name
                  </label>
                  <input
                    type="text"
                    name="middle_name"
                    value={formData.middle_name}
                    onChange={handleInputChange}
                    style={{
                      width: '90%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 6,
                      fontSize: 14,
                      outline: 'none',
                    }}
                    placeholder="Middle name (optional)"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: 14 }}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '90%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 6,
                      fontSize: 14,
                      outline: 'none',
                    }}
                    placeholder="Last name"
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: 14 }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: '90%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 6,
                      fontSize: 14,
                      outline: 'none',
                    }}
                    placeholder="officer@email.com"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontWeight: 'bold', fontSize: 14 }}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    style={{
                      width: '90%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 6,
                      fontSize: 14,
                      outline: 'none',
                    }}
                    placeholder="0241234567 (optional)"
                  />
                </div>
              </div>

              {checkingExistence && (
                <div style={{
                  marginTop: 12,
                  padding: '8px 12px',
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffeaa7',
                  borderRadius: 4,
                  fontSize: 14,
                  color: '#856404'
                }}>
                  Checking if officer already exists...
                </div>
              )}
            </div>
          )}

          {/* Assignment Information Section */}
          <div style={{ marginBottom: 24 }}>
            <h3 style={{
              margin: '0 0 16px 0',
              color: '#333',
              fontSize: 18,
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              paddingBottom: 8,
              borderBottom: '2px solid #f0f0f0'
            }}>
              <MdLocationOn size={22} style={{ color: '#ff9800' }} />
              Assignment Information
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
              <div>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginBottom: 8,
                  fontWeight: 'bold',
                  color: '#333',
                  fontSize: 14,
                }}>

                  Assigned Zone *
                </label>
                <select
                  name="zone_id"
                  value={formData.zone_id}
                  onChange={handleInputChange}
                  required
                  disabled={dataLoading}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: 8,
                    fontSize: 14,
                    outline: 'none',
                    boxSizing: 'border-box',
                    backgroundColor: dataLoading ? '#f5f5f5' : 'white',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#ff9800'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                >
                  <option value="">
                    {dataLoading ? 'Loading zones...' : 'Select a zone'}
                  </option>
                  {zones.map(zone => (
                    <option key={zone.zone_id} value={zone.zone_id}>
                      {zone.zone_name} {zone.zone_code ? `(${zone.zone_code})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginBottom: 8,
                  fontWeight: 'bold',
                  color: '#333',
                  fontSize: 14,
                }}>
                  <MdDateRange size={16} style={{ color: '#ff9800' }} />
                  Assignment Date *
                </label>
                <input
                  type="date"
                  name="assignment_date"
                  value={formData.assignment_date}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: 8,
                    fontSize: 14,
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#ff9800'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                />
              </div>
            </div>
          </div>

          {/* Assignment Info */}
          <div style={{
            padding: 12,
            backgroundColor: '#e1f5fe',
            borderRadius: 6,
            marginBottom: 20,
            fontSize: 13,
            color: '#0277bd',
          }}>
            <strong>Note:</strong> This revenue officer will be created and assigned to the selected zone within your MMDA region.
          </div>

          {/* Buttons */}
          <div style={{
            display: 'flex',
            gap: 10,
            justifyContent: 'flex-end',
          }}>
            <button
              type="button"
              onClick={handleClose}
              style={{
                padding: '10px 20px',
                border: '1px solid #ccc',
                borderRadius: 4,
                backgroundColor: 'white',
                color: '#666',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 'bold',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || dataLoading || !formData.user_id || !formData.zone_id}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderRadius: 8,
                background: (loading || dataLoading || !formData.user_id || !formData.zone_id)
                  ? '#ccc'
                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                cursor: (loading || dataLoading || !formData.user_id || !formData.zone_id) ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'transform 0.2s',
              }}
              onMouseEnter={(e) => {
                if (!e.target.disabled) {
                  e.target.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
              }}
            >
              <MdPerson size={16} />
              {loading ? 'Assigning Officer...' : 'Assign Revenue Officer'}
            </button>
          </div>
        </form>
      </div>

      {/* Conflict Resolution Modal */}
      {showConflictModal && conflictData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: 12,
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 20,
              paddingBottom: 15,
              borderBottom: '2px solid #e0e0e0',
            }}>
              <h2 style={{ margin: 0, color: '#ff6b6b', fontSize: 20 }}>
                ⚠️ Officer Conflicts Detected
              </h2>
              <button
                onClick={() => {
                  setShowConflictModal(false);
                  setConflictData(null);
                  setLoading(false);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 5,
                  borderRadius: 4,
                }}
              >
                <MdClose size={24} style={{ color: '#666' }} />
              </button>
            </div>

            <div style={{ marginBottom: 20 }}>
              <p style={{ margin: '0 0 16px 0', fontSize: 16, color: '#333' }}>
                The following conflicts were found with existing revenue officers:
              </p>

              {conflictData.conflicts.emailConflict && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#fff5f5',
                  border: '1px solid #fed7d7',
                  borderRadius: 6,
                  marginBottom: 12
                }}>
                  <strong style={{ color: '#e53e3e' }}>Email Conflict:</strong>
                  <p style={{ margin: '4px 0 0 0', fontSize: 14 }}>
                    Email "{conflictData.conflicts.emailConflict.email}" is already used by{' '}
                    <strong>{conflictData.conflicts.emailConflict.full_name}</strong> in{' '}
                    {conflictData.conflicts.emailConflict.zone_name}
                  </p>
                </div>
              )}

              {conflictData.conflicts.phoneConflict && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#fff5f5',
                  border: '1px solid #fed7d7',
                  borderRadius: 6,
                  marginBottom: 12
                }}>
                  <strong style={{ color: '#e53e3e' }}>Phone Conflict:</strong>
                  <p style={{ margin: '4px 0 0 0', fontSize: 14 }}>
                    Phone "{conflictData.conflicts.phoneConflict.phone}" is already used by{' '}
                    <strong>{conflictData.conflicts.phoneConflict.full_name}</strong> in{' '}
                    {conflictData.conflicts.phoneConflict.zone_name}
                  </p>
                </div>
              )}

              {conflictData.conflicts.nameConflict && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#fff5f5',
                  border: '1px solid #fed7d7',
                  borderRadius: 6,
                  marginBottom: 12
                }}>
                  <strong style={{ color: '#e53e3e' }}>Name Conflict:</strong>
                  <p style={{ margin: '4px 0 0 0', fontSize: 14 }}>
                    Name "{conflictData.conflicts.nameConflict.full_name}" already exists in{' '}
                    {conflictData.conflicts.nameConflict.zone_name}
                  </p>
                </div>
              )}

              <div style={{
                padding: '12px',
                backgroundColor: '#f7fafc',
                border: '1px solid #cbd5e0',
                borderRadius: 6,
                marginTop: 16
              }}>
                <p style={{ margin: 0, fontSize: 14, color: '#4a5568' }}>
                  <strong>Options:</strong>
                </p>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: 20, fontSize: 14, color: '#4a5568' }}>
                  <li>Click "Cancel" to go back and modify the details</li>
                  </ul>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowConflictModal(false);
                  setConflictData(null);
                  setLoading(false);
                }}
                style={{
                  padding: '10px 20px',
                  border: '2px solid #e2e8f0',
                  borderRadius: 6,
                  backgroundColor: 'white',
                  color: '#4a5568',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontWeight: 'bold',
                }}
              >
                Cancel
              </button>

              {/* Only show proceed button if conflicts allow it */}
              {(!conflictData.conflicts.phoneConflict && !conflictData.conflicts.nameConflict) && (
                  ''
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddRevenueOfficerModal;
